import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { track } from '../analytics';
import { useTranslation } from 'react-i18next';
import { BRAND_TAGLINE, BRAND_URL, DEMO_HOST, BASE_DOMAIN } from '../branding';
import BrandLogo from '../components/BrandLogo';

function LangSwitcher() {
  const { i18n } = useTranslation();
  const switchLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('pm_lang', lng);
    track('login_lang_switched', { lang: lng });
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 2,
        border: '1px solid var(--border, #e0e0e8)',
        borderRadius: 8, padding: 2,
      }}>
        {['en', 'ru'].map(lng => (
          <button
            key={lng}
            onClick={() => switchLang(lng)}
            style={{
              padding: '4px 10px',
              borderRadius: 6, border: 'none',
              fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
              background: i18n.language === lng ? 'var(--text)' : 'transparent',
              color: i18n.language === lng ? '#fff' : 'var(--text3)',
              transition: 'background 0.15s',
            }}
          >
            {lng.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const params  = new URLSearchParams(window.location.search);
  const reason  = params.get('reason');
  const urlLang = params.get('lang');
  const isDemoHost = window.location.hostname === DEMO_HOST;
  const isDemo  = isDemoHost;

  // Применяем язык из ?lang= если передан
  useEffect(() => {
    if (urlLang && ['ru', 'en'].includes(urlLang) && i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
      localStorage.setItem('pm_lang', urlLang);
    }
  }, []); // eslint-disable-line

  const infoMessage = reason === 'deleted' ? t('login.accountDeactivated')
    : reason === 'session_expired' ? t('login.sessionExpired')
    : null;

  const [form, setForm] = useState({ email: params.get('email') || '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // window.__BRANDING__ инжектируется сервером в /render для тенантских субдоменов —
  // читаем синхронно, чтобы логотип тенанта появился уже на первом рендере без мигания.
  const [branding, setBranding] = useState(() =>
    (typeof window !== 'undefined' && window.__BRANDING__?.tenant) ? window.__BRANDING__ : null
  );

  useEffect(() => {
    if (branding) return; // уже получили от сервера, fetch не нужен
    const base = process.env.REACT_APP_API_URL || '/api';
    fetch(`${base}/branding`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.tenant) setBranding(data); })
      .catch(() => {});
  }, []); // eslint-disable-line

  const set = (k, v) => { setError(''); setForm(f => ({ ...f, [k]: v })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email.trim()) return setError(t('login.errorEmailRequired'));
    if (!form.password) return setError(t('login.errorPasswordRequired'));
    setLoading(true);
    track('login_submitted');
    try {
      await login(form.email, form.password);
      const redirectTo = new URLSearchParams(window.location.search).get('redirect');
      navigate(redirectTo || '/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{
      minHeight: '100vh',
      padding: '60px 16px 40px',
      boxSizing: 'border-box',
    }}>
      <div style={{ width: '100%', maxWidth: 380, margin: '0 auto' }}>

        {/* Демо-баннер */}
        {isDemo && (
          <div style={{
            background: 'rgba(99,102,241,0.08)',
            border: '1px solid rgba(99,102,241,0.25)',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 24,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent, #6366f1)', marginBottom: 4 }}>
              {t('login.demoBanner')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>
              {t('login.demoBannerSub')}
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {branding ? (
            <img
              src={`${branding.logo_full_path}?v=${Date.now()}`}
              alt={branding.name}
              style={{ height: 64, width: 'auto', maxWidth: '100%', objectFit: 'contain', margin: '0 auto 20px', display: 'block' }}
            />
          ) : (
            <>
              <a href={`${BRAND_URL}${i18n.language === 'ru' ? '?lang=ru' : ''}`}>
                <BrandLogo height={72} style={{ margin: '0 auto 16px' }} />
              </a>
              {BRAND_TAGLINE !== '' && (
                <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 0, lineHeight: 1.4 }}>
                  {(BRAND_TAGLINE || t('login.tagline')).split('\n').map((line, i, arr) => (
                    <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                  ))}
                </p>
              )}
            </>
          )}
        </div>

        {/* Form */}
        <div className="card">
          <form onSubmit={handleSubmit} noValidate autoComplete="on">
            <div className="form-group">
              <label className="form-label">{t('login.labelEmail')}</label>
              <input
                className="form-input"
                type="email"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                placeholder="email@example.com"
                autoComplete="username"
                // iOS капитализирует первую букву — с этого начинались логины,
                // которые «не подходят» при верном пароле.
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus={!form.email}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('login.labelPassword')}</label>
              <input
                className="form-input"
                type="password"
                value={form.password}
                onChange={e => set('password', e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                autoFocus={!!form.email}
                style={error ? { borderColor: 'var(--red, #ef4444)' } : {}}
              />
              {!isDemo && (
                <div style={{ textAlign: 'right', marginTop: 6 }}>
                  <Link to="/forgot" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              )}
            </div>

            {infoMessage && (
              <div style={{
                background: 'rgba(251,191,36,0.12)', color: '#b45309',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 13, marginBottom: 12,
              }}>
                {infoMessage}
              </div>
            )}

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 13, marginBottom: 12,
              }}>
                {error}
              </div>
            )}

            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading}
              style={{ width: '100%', justifyContent: 'center', marginTop: 4, height: 40 }}
              data-ph-capture-attribute-page="login"
            >
              {loading ? <span className="spinner" /> : (isDemo ? t('login.submitDemo') : t('login.submit'))}
            </button>
          </form>
        </div>

        {/* Ссылки */}
        {!isDemo && (
          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text2)' }}>
            {t('login.noAccount')}{' '}
            <Link to="/register" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              {t('login.register')}
            </Link>
            <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
            <Link to="/demo" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              {t('login.tryDemo')}
            </Link>
          </div>
        )}

        <LangSwitcher />

        <p style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: 'var(--text3)' }}>
          powered by {BASE_DOMAIN}
        </p>
      </div>
    </div>
  );
}
